import { Landmark } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Stepper } from '../../../../components/Stepper';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import {
  TdDate,
  TdInput,
  TdMoney,
  TdSelect,
} from '../../../../components/inline-add-table/cells';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { storageFacilitiesApi } from '../inventoryApi';
import { ITEM_KIND, OWNERSHIP } from '../inventoryTypes';
import type {
  InventoryItemKind,
  StorageFacilityOption,
} from '../inventoryTypes';
import { stockUnitsApi } from '../stockApi';
import { EMPTY_GUID } from '../stockTypes';
import type { RegisterStockUnitLine } from '../stockTypes';
import { sampleStockUnitRow } from '../utils/sampleStock';

/**
 * What a registration is recorded AGAINST.
 *
 * Deliberately the narrowest set of fields the form reads rather than a stock level: a level row
 * satisfies it structurally, and so does an item registered five seconds ago that has no level
 * row yet. The second case is what the item form's "add stock now" tick hands over, and it is
 * the reason this is a shape rather than a type name — the same call
 * {@link import('./StockAdjustmentModal').StockAdjustmentTarget} takes for the quantity half.
 *
 * `onHand` is 0 for a fresh registration, which is not a placeholder — it is what is true.
 */
export interface StockUnitsRegisterTarget {
  id: string;
  sku: string;
  name: string;
  unitCode: string;
  inventoryCategoryName: string;
  onHand: number;

  /**
   * What the item IS, because one column of the grid only applies to one kind.
   *
   * `TermEndDate`'s own contract is "licence or service term end — SUBSCRIPTIONS only", so on
   * an equipment registration that column can never apply and a control that can never apply is
   * noise. It is on the target rather than fetched because every caller already holds it.
   */
  kind: InventoryItemKind;
}

/** One row of the grid. `key` is React's, never sent. */
interface UnitRow {
  key: string;
  storageFacilityId: string;
  serialNumber: string;
  acquisitionDate: string;
  termEndDate: string;

  /* Both drawn only on a LEASED registration — see the tenure banner. */
  leaseEndDate: string;
  leasedFrom: string;
  fund: string;

  unitCost: number | null;
  notes: string;
}

function blankRow(): UnitRow {
  return {
    key: uid(),
    storageFacilityId: '',
    serialNumber: '',
    acquisitionDate: '',
    termEndDate: '',
    leaseEndDate: '',
    leasedFrom: '',
    fund: '',
    unitCost: null,
    notes: '',
  };
}

/**
 * TWO STEPS — product call, 23 Sep 2026, and it reverses the one-panel shape this form
 * shipped with.
 *
 * That call refused an Ownership step on the ground that "what is left of it is two fields",
 * which was true while the item was stated somewhere else on the same panel. It is not true of
 * the step as it stands: step one carries WHAT is being registered and UNDER WHAT TERMS — the
 * item as a read-only statement, the tenure, and the lease term — which is a stage of work
 * rather than a dropdown and a date.
 *
 * It also puts the reference back where it was: its own wizard separates Item Selection and
 * Stock Ownership from Stock Detail. What stays refused is its SUMMARY step, because a stepped
 * create form here does not end on one.
 */
const STEPS = [
  { key: 'item', label: 'Item & ownership' },
  { key: 'units', label: 'Stock entry' },
] as const;

/** `serialNumber` → `serial-number`, matching `focusValidationError`'s own kebab. */
function kebab(field: string): string {
  return field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * The CONTRACT with the server, and both ends have to stay identical.
 *
 * A registration row has no field a reader could otherwise be pointed at, so the server rejects
 * a cell as `units.2.serialNumber` and every cell here carries the matching id.
 * `focusValidationError` joins them with `${idPrefix}-${kebab(field)}`. Change either side and
 * the reader is told which cell is wrong while nothing moves — which reads as the feature not
 * being wired at all.
 */
const cellId = (index: number, column: string) =>
  `stock-units-register-units.${index}.${kebab(column)}`;

/**
 * Register SEVERAL serialized units of one item at once.
 *
 * **Captured from the hospital's Register Stock Units flow on 22 Sep 2026**, and it fills the
 * hole the stock level screen had for a serialized item: a lot correction does not apply to one,
 * so `Stock adjustment` is absent from those rows and the only path was twelve trips through the
 * single-unit form on another page.
 *
 * **It writes RECEIPTS, not adjustments**, which is the whole difference from its sibling. The
 * quantity half says a figure was wrong and records a signed `Adjustment`; this says stock
 * arrived and records one `Receipt` per unit, exactly as the single create does.
 *
 * **It is ALL-OR-NOTHING**, and that is why it posts one request rather than looping the single
 * create. Eight units written beside a ninth refused would leave the reader working out which
 * four are missing and re-keying them against a serial check that now refuses the eight already
 * in.
 *
 * ## Where it DIVERGES from the reference, and why
 *
 * The hospital's form is a four-step wizard — Item Selection, Stock Ownership, Stock Detail,
 * Summary. This is TWO steps, and each difference is a rsys_ rule winning over the layout,
 * which is what the suite's precedence rule says happens:
 *
 * - **No Summary step.** A stepped create form here does not end on one (product call,
 *   15 Sep 2026): it re-states every value the reader just typed, one click after typing it,
 *   and is not a second chance to edit. The confirm tick it would have carried is on the last
 *   step of fields.
 * - **Item Selection and Stock Ownership are ONE step**, because the item is not selected here:
 *   the reader chose it by opening a row's kebab, so it is STATED as a read-only fact rather
 *   than offered as a picker that can only hold one value. What is left to ask on that step is
 *   the tenure, which is the registration's own — one registration is one purchase or one lease.
 * - **Stock Detail is the second**, and it is the grid.
 *
 * **It was ONE panel until 23 Sep 2026**, on the argument that an ownership step would be two
 * fields and the rail would promise a stage of work that is a dropdown and a date. That was a
 * fair reading of the step in isolation and a poor one of the step as built: it carries WHAT is
 * being registered and UNDER WHAT TERMS, which is a stage. Recorded as a reversal rather than
 * quietly rewritten.
 *
 * **What is kept is the PROCESS**, which is what the reference contributes: one registration is
 * one purchase or one lease agreement, so tenure is shared by every unit in it and a mixed
 * delivery is two registrations. That sentence is the reference's own.
 *
 * ## What is deliberately NOT collected
 *
 * The hospital captures a **fund**, an auto-assigned **property number** and an ICS/PAR
 * **classification** per unit. None is built here: `StockUnit` carries no fund column, no
 * property number and no classification, and the ICS/PAR threshold is one of the unbuilt
 * settings knobs. Adding any of them is a migration and a product decision, not a field to slip
 * into this form — said out loud so the absence reads as a gap rather than an oversight.
 */
export function StockUnitsRegisterModal({
  open,
  target,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** What the units are of. Null closes the form. */
  target: StockUnitsRegisterTarget | null;
  onClose: () => void;
  onSaved: (registered: number) => void;
}) {
  /*
    TENURE is the registration's, not the row's — one registration is one purchase or one lease
    agreement.

    **REQUIRED, and seeded OWNED — product call, 23 Sep 2026**, reversing the empty seed this
    form shipped with. That seed meant "fall back to the catalog item's own declaration", and
    the catalog stopped declaring one on the same day: the item form no longer asks, so there
    is nothing left to fall back TO and an empty answer would leave the tenure unstated on the
    one form that owns it.

    **A default of Owned is not the "pre-selected answer nobody gave" failure**, which is about
    a value whose default ASSERTS something. Owned is what the whole product already reads a
    null as — `ApplyTenure`, `StockUnit.Ownership`, the Equipment tab — so the default states
    what was already true rather than inventing a claim, and a leased delivery is one click.
  */
  /*
    ONE TICK, CHECKED by default — product call, 28 Sep 2026, replacing the required
    `<Select>` this form shipped with.

    The tenure has exactly two states, and a dropdown over two values asks a yes/no question
    in three clicks. Ticked is OWNED, which is what the whole product already reads a null
    tenure as — `ApplyTenure`, `StockUnit.Ownership`, the Equipment tab — so the default
    states what was already true rather than inventing a claim.

    <b>It is NOT the "pre-selected answer nobody gave" failure.</b> That rule is about a
    default that ASSERTS something — `IsGovernmentService` is the worked case, where false
    claims the post was private employment. Owned asserts nothing this product was not
    already assuming, and a leased delivery is one click.
  */
  const [owned, setOwned] = useState(true);

  const [rows, setRows] = useState<UnitRow[]>([blankRow()]);

  const [stepIndex, setStepIndex] = useState(0);

  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const itemId = target?.id ?? null;

  useEffect(() => {
    if (!open || !itemId) return;

    setOwned(true);
    setRows([blankRow()]);
    setStepIndex(0);
    setConfirmed(false);
    setError(null);

    const controller = new AbortController();
    setLoading(true);

    storageFacilitiesApi
      .options(controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) setFacilities(loaded);
      })
      // A failed feed names no field, so it must not reach the save path's error state. The
      // picker is simply empty, and the reader can see there is nothing to choose.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, itemId]);

  const facilityOptions = useMemo(
    () => facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.code })),
    [facilities],
  );

  const patch = (key: string, next: Partial<UnitRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...next } : row)));

  /*
    ABANDONED vs HALF-FILLED — the client's mirror of the server's own rule, and the two must not
    drift. A row nobody touched is dropped; one carrying a facility, a date or a cost and no
    serial is refused, because that one is a row somebody started.
  */
  const isAbandoned = (row: UnitRow) =>
    row.serialNumber.trim() === '' &&
    row.storageFacilityId === '' &&
    row.acquisitionDate === '' &&
    row.termEndDate === '' &&
    row.unitCost === null &&
    row.notes.trim() === '';

  const filled = rows.filter((row) => !isAbandoned(row));

  /*
    A row is COMPLETE when it carries both required cells. The `+` is gated on the last row being
    complete, so a misclick cannot spawn a second blank one — and an abandoned row counts as
    complete for that purpose, because the reader is entitled to leave it and press Cancel.
  */
  const lastRow = rows[rows.length - 1];
  const lastIncomplete =
    lastRow !== undefined &&
    !isAbandoned(lastRow) &&
    (lastRow.serialNumber.trim() === '' || lastRow.storageFacilityId === '');

  /*
    WHAT EACH STEP OWES, named so Next and Save can each say what is missing rather than
    sitting dead — the rule a stepped form's disabled primary already carries.

    Step 1 is ALWAYS answerable now that the tenure is a TICK rather than a picker — there is
    no empty state left for it to have, so nothing on that step can block Next. The function
    stays because the step machinery reads one per step; it simply has nothing to report.
  */
  const stepOneBlocked = (): string | null => null;

  const isLastStep = stepIndex === STEPS.length - 1;

  /** Only a SUBSCRIPTION seat has a licence term, so nothing else draws the column. */
  const tracksTerm = target?.kind === ITEM_KIND.subscription;

  /*
    A NEW ROW CARRIES THE LAST ONE FORWARD — the facility, the acquisition date, the cost and
    the note.

    A delivery is one consignment: twelve laptops arrive on one date, at one store, at one price,
    and re-answering four identical questions twelve times is the work this grid exists to
    remove. It is the item form's unit suggestion one surface along, and the same rule governs
    it — a SUGGESTION, seeded once and the reader's from then on, never re-applied.

    **THE SERIAL IS NEVER CARRIED**, and that is the whole point of the column: it is held once
    per item, so copying it would seed a duplicate the form then refuses on the row it just made.

    **Nor is the TERM END**, which is the one field here that is genuinely per-seat — that is the
    argument its own column makes for living on the unit rather than on the item.

    It reads the last row carrying ANYTHING rather than the last row: a reader who pressed `+`
    twice would otherwise copy the blank they abandoned.
  */
  const addRow = () =>
    setRows((current) => {
      const last = [...current].reverse().find((row) => !isAbandoned(row));

      return [
        ...current,
        {
          ...blankRow(),
          ...(last === undefined
            ? {}
            : {
                storageFacilityId: last.storageFacilityId,
                acquisitionDate: last.acquisitionDate,
                unitCost: last.unitCost,
                notes: last.notes,
              }),
        },
      ];
    });

  /** Named for the Save tooltip. Null means nothing is in the way. */
  const blockedReason = (): string | null => {
    /*
      The EARLIER step first, and named as a step. A reader standing on the grid with an
      unanswered tenure behind them needs to be told where to go, not only what is missing —
      which is the rule the item form's own Save reason follows.
    */
    const first = stepOneBlocked();
    if (first !== null) return `${first} It is on the Item & ownership step.`;

    if (filled.length === 0) return 'Fill in at least one unit to register.';

    const incomplete = filled.findIndex(
      (row) => row.serialNumber.trim() === '' || row.storageFacilityId === '',
    );

    if (incomplete >= 0) {
      return `Row ${rows.indexOf(filled[incomplete]!) + 1} needs a facility and a serial number.`;
    }

    /*
      The duplicate check runs HERE as well as on the server, because a serial repeated inside
      one registration is the one collision no index can catch before the insert — and telling
      the reader before they press Save beats a round trip that refuses the whole batch.
      Case-insensitive, because SQL Server's default collation is.
    */
    const seen = new Set<string>();

    for (const row of filled) {
      const serial = row.serialNumber.trim().toLowerCase();

      if (seen.has(serial)) {
        return `Row ${rows.indexOf(row) + 1} repeats a serial number already on an earlier row.`;
      }

      seen.add(serial);
    }

    if (!confirmed) return 'Tick the confirmation to register these units.';

    return null;
  };

  const canSave = !saving && blockedReason() === null;

  const save = async () => {
    if (!target) return;

    setSaving(true);
    setError(null);

    try {
      /*
        EVERY row is sent, abandoned ones included, so the index a rejection names is the index of
        the row on screen. Dropping them here would shift every later row's number and point the
        reader at the wrong cell; the server drops them after validating.
      */
      const units: RegisterStockUnitLine[] = rows.map((row) => ({
        // `EMPTY_GUID`, never `''` — see the constant. An empty string fails model binding and
        // takes the whole registration with it, so the server's abandoned-row rule never runs.
        storageFacilityId: row.storageFacilityId || EMPTY_GUID,
        serialNumber: row.serialNumber.trim(),
        acquisitionDate: row.acquisitionDate || null,
        termEndDate: row.termEndDate || null,

        /*
          THE THREE LICENCE FIELDS ARE NULL HERE, and that is the split rather than an omission:
          who a seat is assigned to and how it is billed are questions about a LICENCE, and this
          form registers physical stock. `<LicenseRegisterModal>` collects them, and the server
          refuses all three on anything but a digital asset — so sending a value from here would
          be refused rather than ignored.
        */
        accountablePersonnelId: null,

        /*
          CLEARED where the tick says owned, so a reader who filled these in and then ticked
          the box cannot leave a contract on the row. The server clears them too — this is the
          half that keeps the request honest about what the form is showing.
        */
        leaseEndDate: owned ? null : row.leaseEndDate || null,
        leasedFrom: owned ? null : row.leasedFrom.trim() || null,

        // NOT cleared by the tenure: a fund is what the purchase was charged to, which is as
        // true of a lease as of an outright buy.
        fund: row.fund.trim() || null,

        unitCost: row.unitCost,
        paymentMode: null,
        firstBillingDate: null,

        notes: row.notes.trim() || null,
      }));

      const registered = await stockUnitsApi.register({
        inventoryItemId: target.id,

        /*
          The TICK is the tenure, and it is still HEADER-SCOPED: one registration is one
          purchase or one lease agreement. The lease END and the LESSOR are the rows’,
          cleared above where the tick says owned.
        */
        ownership: owned ? OWNERSHIP.owned : OWNERSHIP.leased,

        units,
      });

      onSaved(registered);
      onClose();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('These units could not be registered.', 0);

      setError(failure);
      /*
        A GRID CELL IS NOT IN THE DOM UNTIL ITS STEP RENDERS, which is exactly the problem
        `stepOf`/`onStep` solve. Without them the server names `units.2.serialNumber`, the
        reader is standing on step 1, and nothing moves — which reads as the feature not being
        wired at all.
      */
      focusValidationError(failure, {
        idPrefix: 'stock-units-register',
        stepOf: (field) => (field.startsWith('units') ? 1 : 0),
        onStep: setStepIndex,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!target) return null;

  const unit = target.unitCode;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Register stock units"
      id="stock-units-register"
      size="3xl"
      autogenerate={{
        onGenerate: () => {
          /*
            Fills the ROWS, and leaves the tenure alone. Ownership reveals the lease field, so
            choosing one would make the same button produce a different form on each click —
            the step-adding-flag rule, and the same call both stock generators already take.

            THREE rows, because one would exercise nothing this form exists for: the grid, the
            duplicate check and the row numbering all only mean something past one.

            It also MOVES TO THE GRID, because that is where it just filled something in. A
            generator that populated a step the reader cannot see is a button that looks like it
            did nothing.
          */
          setRows([0, 1, 2].map(() => ({ ...blankRow(), ...sampleStockUnitRow(facilities) })));
          setStepIndex(1);
        },
        disabled: saving || loading,
      }}
      /*
        THE LAST STEP ONLY, and that is the caller's job rather than the shell's: `<Modal>`
        renders the strip whenever the prop is set and cannot know what a step is. The strip
        APPEARING is the signal that there is nothing left to fill in, so one present from step
        one teaches the reader it is furniture.
      */
      confirm={
        isLastStep
          ? {
              id: 'stock-units-register-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      footer={
        <>
          <Button
            id="stock-units-register-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          {/* Back pairs with Next and renders from the second step on. NEVER gated on validity:
              going back is how an invalid step gets fixed. */}
          {stepIndex > 0 ? (
            <Button
              id="stock-units-register-back"
              variant="secondary"
              onClick={() => setStepIndex(0)}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {!isLastStep ? (
            <Tooltip disabledReason={stepOneBlocked()}>
              <Button
                id="stock-units-register-next"
                variant="secondary"
                onClick={() => setStepIndex(1)}
                disabled={stepOneBlocked() !== null}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip disabledReason={blockedReason()}>
            <Button
              id="stock-units-register-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex min-h-0 flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Stepper
          id="stock-units-register"
          steps={STEPS.map((step) => ({ key: step.key, label: step.label }))}
          activeIndex={stepIndex}
          /* Forward only from a step that is answered; back is free, exactly as Back is. */
          onJump={(index) => {
            if (index <= stepIndex || stepOneBlocked() === null) setStepIndex(index);
          }}
        />

        <div hidden={stepIndex !== 0} className="flex flex-col gap-4">
        {/*
          The ITEM, stated rather than asked for. The reader chose it by opening a row's kebab,
          so a picker here could only ever hold one value — the locked-parent rule, and the Table
          Display View is what read-only facts take.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            /*
              ONE ROW for the item's identity, joined with the ` — ` this product already uses
              to join a qualifier to a name — the same merge `<StockAdjustmentModal>` makes for
              `sku — name`, widened by the category.

              It is a deliberate departure from the Table Display View's "one fact per row":
              three rows to restate which item the form was opened FOR pushed the fields the
              reader actually came to fill further down, and none of the three identifies the
              item on its own.
            */
            {
              label: 'Item',
              value: `${target.sku} — ${target.name} — ${target.inventoryCategoryName}`,
              mono: true,
            },
            { label: 'On hand', value: `${target.onHand} ${unit}`, mono: true },
          ]}
        />

        {/*
          THE TENURE, as a tick in a banner rather than a picker — product call, 28 Sep 2026.

          It is still HEADER-SCOPED, and that half did not move: one registration is one
          purchase or one lease agreement, so a mixed delivery is two registrations. What DID
          move onto the rows is WHEN each unit goes back and WHO it came from — the two columns
          the grid draws while this is unticked.

          `tone="slate"` because nothing is wrong and nothing is in progress: it states the
          terms the delivery is recorded under. Amber would put an attention-coloured strip on
          every ordinary purchase, which is the row a reader learns to skip.
        */}
        <NextActionAlert
          id="stock-units-register-ownership"
          tone="slate"
          icon={<Landmark className="h-4 w-4" aria-hidden="true" />}
          title={owned ? 'These units are owned outright' : 'These units are leased or rented'}
          description={
            <>
              Every unit in this registration shares it — one registration is one purchase or one
              lease agreement, so a mixed delivery is two registrations.
              {owned ? null : (
                <>
                  {' '}
                  The grid now asks when each unit goes back and who it is leased from, because
                  three rented vans can come back on three days from three hire companies.
                </>
              )}
              <span className="mt-2 flex items-center gap-2 border-t border-slate-200 pt-2">
                <Checkbox
                  id="stock-units-register-owned"
                  checked={owned}
                  onChange={(e) => setOwned(e.target.checked)}
                  disabled={saving}
                />
                <label htmlFor="stock-units-register-owned" className="cursor-pointer text-xs">
                  Owned outright. Untick where they are leased or rented.
                </label>
              </span>
            </>
          }
        />
        </div>

        <div hidden={stepIndex !== 1} className="flex min-h-0 flex-col gap-4">
        <InlineAddTable
          /*
            THE LEASE PAIR IS DRAWN ONLY WHILE THE TENURE SAYS LEASED, and the headers and the
            cells read one flag so a column can never appear without its heading — which would
            shift every column after it by one.

            On an OWNED registration they would be two columns of em dashes on every row: a
            column saying one thing on every row is worse than a missing one.
          */
          headers={[
            'Facility *',
            'Serial no. *',
            'Acquired',
            'Fund',
            ...(tracksTerm ? ['Term ends'] : []),
            ...(owned ? [] : ['Lease ends', 'Leased from']),
            'Unit cost',
            'Notes',
          ]}
          colWidths={[
            200,
            200,
            150,
            170,
            ...(tracksTerm ? [150] : []),
            ...(owned ? [] : [150, 200]),
            140,
            260,
          ]}
          /*
            THE KEY CARRIES THE COLUMN SET, because this grid's columns COME AND GO.

            `<InlineAddTable>` stores widths by POSITION — `col-0`, `col-1` — which it may,
            because an inline-add table's columns are declared in order and never reordered.
            This one is the exception the comment does not cover: the lease pair is drawn only
            on a leased registration and the term column only for a kind that has one, so
            `col-5` is Notes on an owned grid and Lease ends on a leased one.

            Shared, one reader's dragged Notes width lands on somebody else's Lease ends — the
            positional-storage failure the issuance grid's own v2/v3 comment describes, arriving
            through the flags rather than through an edit. Naming the set gives each its own
            layout and leaves the others alone.
          */
          resizeKey={`stock-units-register.${owned ? 'owned' : 'leased'}${tracksTerm ? '.term' : ''}`}
          /* NOTES takes the slack, wherever it lands once the optional columns are counted. */
          flexColumn={5 + (tracksTerm ? 1 : 0) + (owned ? 0 : 2)}
          rowCount={rows.length}
          onAddRow={addRow}
          addDisabled={saving || lastIncomplete}
          addButtonId="stock-units-register-add"
        >
          {rows.map((row, index) => (
            <InlineRow
              key={row.key}
              idx={index + 1}
              onRemove={() =>
                setRows((current) => {
                  const next = current.filter((r) => r.key !== row.key);

                  // Never nothing: an empty grid has no `+` row of its own to press, so removing
                  // the last row would leave the form with no way back to a first one.
                  return next.length > 0 ? next : [blankRow()];
                })
              }
            >
              <TdSelect
                id={cellId(index, 'storageFacilityId')}
                ariaLabel={`Facility for row ${index + 1}`}
                value={row.storageFacilityId || null}
                onChange={(next) => patch(row.key, { storageFacilityId: next })}
                options={facilityOptions}
                remote
                disabled={saving}
              />

              <TdInput
                id={cellId(index, 'serialNumber')}
                ariaLabel={`Serial number for row ${index + 1}`}
                value={row.serialNumber}
                onChange={(next) => patch(row.key, { serialNumber: next })}
                placeholder="Serial no."
                disabled={saving}
              />

              <TdDate
                id={cellId(index, 'acquisitionDate')}
                ariaLabel={`Acquired date for row ${index + 1}`}
                value={row.acquisitionDate}
                onChange={(next) => patch(row.key, { acquisitionDate: next })}
                disabled={saving}
              />

              {/* Withheld for anything but a subscription — see `tracksTerm`. A cell drawn
                  without its header would shift every column after it by one. */}
              {tracksTerm ? (
                <TdDate
                  id={cellId(index, 'termEndDate')}
                  ariaLabel={`Term end date for row ${index + 1}`}
                  value={row.termEndDate}
                  onChange={(next) => patch(row.key, { termEndDate: next })}
                  disabled={saving}
                />
              ) : null}

              {/*
                WHEN THIS ONE GOES BACK, and WHO IT CAME FROM. Per row from 28 Sep 2026 — the
                header could only say one date and one lessor for the whole delivery, which a
                staggered hire agreement cannot record.

                The server CLEARS both where the tenure is owned and the validator REFUSES
                them, which is the pair the eligibility licence fields already carry: refusing
                names the cell, clearing stops a value surviving behind the reader.
              */}
              {/*
                DRAWN ON EVERY ROW, unlike the two below it — a fund is what the purchase was
                charged to, which is as true of a lease as of an outright buy. Free text,
                because nothing aggregates by it yet: the ICS and the PAR print it and a
                person reads it.
              */}
              <TdInput
                id={cellId(index, 'fund')}
                ariaLabel={`Fund for row ${index + 1}`}
                value={row.fund}
                onChange={(next) => patch(row.key, { fund: next })}
                placeholder="General Fund"
                disabled={saving}
              />

              {owned ? null : (
                <>
                  <TdDate
                    id={cellId(index, 'leaseEndDate')}
                    ariaLabel={`Lease end date for row ${index + 1}`}
                    value={row.leaseEndDate}
                    onChange={(next) => patch(row.key, { leaseEndDate: next })}
                    disabled={saving}
                  />

                  <TdInput
                    id={cellId(index, 'leasedFrom')}
                    ariaLabel={`Leased from for row ${index + 1}`}
                    value={row.leasedFrom}
                    onChange={(next) => patch(row.key, { leasedFrom: next })}
                    placeholder="Lessor"
                    disabled={saving}
                  />
                </>
              )}

              <TdMoney
                id={cellId(index, 'unitCost')}
                ariaLabel={`Unit cost for row ${index + 1}`}
                value={row.unitCost}
                onChange={(next) => patch(row.key, { unitCost: next })}
                disabled={saving}
              />

              <TdInput
                id={cellId(index, 'notes')}
                ariaLabel={`Notes for row ${index + 1}`}
                value={row.notes}
                onChange={(next) => patch(row.key, { notes: next })}
                disabled={saving}
              />
            </InlineRow>
          ))}
        </InlineAddTable>

        {/*
          The one sentence the grid cannot carry in a header. Both facts are load-bearing: what a
          serial is held against, and that the tenure above governs every row.
        */}
        <p className="text-xs text-slate-500">
          A serial number is held once per item, so each row names a different one. Every unit
          registered here is received into stock and shares the ownership type on the step before.
        </p>
        </div>
      </div>
    </Modal>
  );
}
